import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import NavHeader from '@/components/NavHeader';
import { useAuth } from '@pooflabs/web';
import { PageLayout } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeAllLocks } from '@/lib/collections/locks';
import type { LocksResponse } from '@/lib/collections/locks';
import { subscribeAllDonations } from '@/lib/collections/donations';
import type { DonationsResponse } from '@/lib/collections/donations';
import { BlurFade } from '@/components/effects/BlurFade';
import { Particles } from '@/components/effects/Particles';
import { ShootingStars } from '@/components/effects/ShootingStars';
import {
  buildLeaderboard,
  formatPoints,
  truncateAddress,
  type LeaderboardEntry,
} from '@/utils/pointsCalculator';
import { Lock, Trophy, Skull, Flame, Crown, Medal, ArrowLeft, Heart, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getTokenByMint, SOL, BONK, SKR, MEW, ANGELIC_SOL, GMT, DRAGON } from '@/utils/tokenConfig';
import { warmUsdPrices } from '@/utils/usdPricing';

// ─── Token Pills ────────────────────────────────────────────────────────────

function TokenPills({ tokenMints }: { tokenMints: Set<string> }) {
  const mints = Array.from(tokenMints);
  if (mints.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {mints.map((mint) => {
        const t = getTokenByMint(mint);
        return (
          <span
            key={mint}
            className="text-xs px-1.5 py-0.5 rounded font-bold"
            style={{
              background: t.brandColorBg,
              color: t.brandColor,
              border: `1px solid ${t.brandColorBorder}`,
              fontSize: '0.65rem',
            }}
          >
            {t.symbol}
          </span>
        );
      })}
    </div>
  );
}

// ─── Podium Card ────────────────────────────────────────────────────────────

interface PodiumCardProps {
  entry: LeaderboardEntry;
  rank: 1 | 2 | 3;
  isMe: boolean;
}

const PODIUM_CONFIG = {
  1: {
    color: 'hsl(43 75% 60%)',
    glow: 'hsl(38 65% 57% / 0.35)',
    bg: 'linear-gradient(145deg, hsl(30 6% 9%) 0%, hsl(38 30% 10%) 100%)',
    border: 'hsl(38 65% 57% / 0.5)',
    label: 'hsl(38 65% 57%)',
    Icon: Crown,
    size: 'lg' as const,
  },
  2: {
    color: 'hsl(210 20% 72%)',
    glow: 'hsl(210 20% 72% / 0.2)',
    bg: 'linear-gradient(145deg, hsl(30 6% 8%) 0%, hsl(210 8% 10%) 100%)',
    border: 'hsl(210 15% 30%)',
    label: 'hsl(210 20% 72%)',
    Icon: Medal,
    size: 'md' as const,
  },
  3: {
    color: 'hsl(28 55% 48%)',
    glow: 'hsl(28 55% 48% / 0.2)',
    bg: 'linear-gradient(145deg, hsl(30 6% 8%) 0%, hsl(28 10% 10%) 100%)',
    border: 'hsl(28 30% 26%)',
    label: 'hsl(28 55% 48%)',
    Icon: Medal,
    size: 'md' as const,
  },
};

const PodiumCard: React.FC<PodiumCardProps> = ({ entry, rank, isMe }) => {
  const cfg = PODIUM_CONFIG[rank];
  const { Icon } = cfg;
  const isFirst = rank === 1;

  return (
    <div
      className="relative rounded-xl p-6 flex flex-col items-center text-center overflow-hidden"
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        boxShadow: `0 0 ${isFirst ? 48 : 24}px ${cfg.glow}`,
        order: rank === 1 ? 0 : rank === 2 ? -1 : 1,
      }}
    >
      {/* Glow gradient top */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 40% at 50% 0%, ${cfg.glow}, transparent)`,
        }}
      />

      {/* "YOU" badge */}
      {isMe && (
        <div
          className="absolute top-3 right-3 text-xs font-bold px-2 py-0.5 rounded-full"
          style={{ background: 'hsl(38 65% 57%)', color: 'hsl(30 8% 4%)' }}
        >
          YOU
        </div>
      )}

      <div className="relative z-10">
        {/* Rank icon */}
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{
            background: `${cfg.color}22`,
            border: `2px solid ${cfg.color}`,
          }}
        >
          <Icon size={isFirst ? 22 : 18} style={{ color: cfg.color }} />
        </div>

        {/* Rank number */}
        <div
          className="text-xs font-bold tracking-widest uppercase mb-2"
          style={{ color: cfg.label }}
        >
          #{rank}
        </div>

        {/* Address */}
        <div
          className="font-mono text-sm mb-1"
          style={{ color: isMe ? 'hsl(43 75% 68%)' : 'hsl(40 15% 82%)' }}
        >
          {truncateAddress(entry.address)}
        </div>

        {/* Perma indicator */}
        {entry.permaCount > 0 && (
          <div
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mb-2"
            style={{
              background: 'hsl(0 65% 48% / 0.15)',
              border: '1px solid hsl(0 65% 48% / 0.3)',
              color: 'hsl(0 75% 65%)',
            }}
          >
            <Skull size={10} />
            {entry.permaCount} PermaLOCK{entry.permaCount > 1 ? 's' : ''}
          </div>
        )}

        {/* Token pills */}
        <div className="flex justify-center mb-3">
          <TokenPills tokenMints={entry.tokenMints} />
        </div>

        {/* Points */}
        <div
          className="font-bold"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: isFirst ? '2.5rem' : '2rem',
            lineHeight: 1.1,
            color: cfg.color,
            textShadow: `0 0 20px ${cfg.glow}`,
          }}
        >
          {formatPoints(entry.totalPoints)}
        </div>
        <div className="text-xs mt-1" style={{ color: 'hsl(35 8% 45%)' }}>
          Selfless Points
        </div>
        {entry.donationPoints > 0 && (
          <div className="text-xs mt-1 flex items-center justify-center gap-1" style={{ color: 'hsl(168 80% 48%)' }}>
            <Heart size={9} />
            +{formatPoints(entry.donationPoints)} donated
          </div>
        )}

        {/* Lock count */}
        <div className="text-xs mt-2" style={{ color: 'hsl(35 8% 40%)' }}>
          {entry.lockCount} lock{entry.lockCount !== 1 ? 's' : ''}
        </div>

        {/* Pending pricing indicator */}
        {entry.pendingCount > 0 && (
          <div
            className="inline-flex items-center gap-1 text-xs mt-1.5"
            style={{ color: 'hsl(35 8% 40%)' }}
            title="Some entries are excluded from the total until a live price is available"
          >
            <Clock size={9} />
            {entry.pendingCount} pending pricing
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Table Row ───────────────────────────────────────────────────────────────

interface TableRowProps {
  entry: LeaderboardEntry;
  rank: number;
  isMe: boolean;
  delay: number;
}

const TableRow: React.FC<TableRowProps> = ({ entry, rank, isMe, delay }) => (
  <BlurFade delay={delay}>
    <div
      className="grid items-center gap-3 px-5 py-3.5 rounded-lg transition-colors"
      style={{
        gridTemplateColumns: '2.5rem 1fr auto auto auto',
        background: isMe
          ? 'hsl(38 65% 57% / 0.08)'
          : 'hsl(30 6% 7%)',
        border: isMe
          ? '1px solid hsl(38 65% 57% / 0.3)'
          : '1px solid hsl(35 12% 12%)',
      }}
    >
      {/* Rank */}
      <span
        className="text-sm font-mono font-bold"
        style={{ color: isMe ? 'hsl(43 75% 68%)' : 'hsl(35 8% 45%)' }}
      >
        {rank}
      </span>

      {/* Address + badges */}
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="font-mono text-sm truncate"
          style={{ color: isMe ? 'hsl(43 75% 68%)' : 'hsl(40 15% 80%)' }}
        >
          {truncateAddress(entry.address)}
        </span>
        {isMe && (
          <span
            className="text-xs font-bold px-1.5 py-0.5 rounded shrink-0"
            style={{ background: 'hsl(38 65% 57%)', color: 'hsl(30 8% 4%)' }}
          >
            YOU
          </span>
        )}
        {entry.permaCount > 0 && (
          <span
            className="inline-flex items-center gap-1 text-xs shrink-0"
            style={{ color: 'hsl(0 75% 60%)' }}
          >
            <Flame size={11} />
          </span>
        )}
        {entry.hasDonated && (
          <span
            className="inline-flex items-center gap-1 text-xs shrink-0"
            style={{ color: 'hsl(168 80% 48%)' }}
            title="Has donated to others"
          >
            <Heart size={11} />
          </span>
        )}
        {entry.pendingCount > 0 && (
          <span
            className="inline-flex items-center gap-1 text-xs shrink-0"
            style={{ color: 'hsl(35 8% 45%)' }}
            title={`${entry.pendingCount} ${entry.pendingCount === 1 ? 'entry is' : 'entries are'} excluded from the total until a live price is available`}
          >
            <Clock size={11} />
          </span>
        )}
        <div className="hidden sm:flex shrink-0">
          <TokenPills tokenMints={entry.tokenMints} />
        </div>
      </div>

      {/* Perma count */}
      <div className="text-right hidden sm:block">
        {entry.permaCount > 0 ? (
          <span
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
            style={{
              background: 'hsl(0 65% 48% / 0.12)',
              color: 'hsl(0 75% 65%)',
            }}
          >
            <Skull size={9} />
            {entry.permaCount}
          </span>
        ) : (
          <span className="text-xs" style={{ color: 'hsl(35 8% 30%)' }}>
            —
          </span>
        )}
      </div>

      {/* Lock count */}
      <div
        className="text-xs text-right hidden md:block"
        style={{ color: 'hsl(35 8% 45%)' }}
      >
        {entry.lockCount}
      </div>

      {/* Points */}
      <div
        className="text-right font-bold font-mono"
        style={{
          color: isMe ? 'hsl(43 75% 68%)' : 'hsl(38 65% 57%)',
          fontSize: '0.95rem',
        }}
      >
        {formatPoints(entry.totalPoints)}
      </div>
    </div>
  </BlurFade>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

export const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Points below read from usdPricing's module-level cache synchronously, which
  // isn't itself reactive — warm it (staggered, so we don't burst Jupiter) and
  // force a re-render as each price resolves so points update out of "pending".
  const [, bumpPriceTick] = useState(0);
  useEffect(() => {
    const cancel = warmUsdPrices([SOL, BONK, SKR, MEW, ANGELIC_SOL, GMT, DRAGON], () => {
      bumpPriceTick((n) => n + 1);
    });
    return cancel;
  }, []);

  const { data: allLocks, loading } = useRealtimeData<LocksResponse[]>(
    subscribeAllLocks,
    true
  );

  const { data: allDonations } = useRealtimeData<DonationsResponse[]>(
    subscribeAllDonations,
    true
  );

  const locks = allLocks ?? [];
  const donations = allDonations ?? [];
  const leaderboard = buildLeaderboard(locks, donations);
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3, 100);

  const myAddress = user?.address;
  const myRank = myAddress
    ? leaderboard.findIndex((e) => e.address === myAddress) + 1
    : 0;
  const myEntry = myAddress ? leaderboard.find((e) => e.address === myAddress) : null;

  return (
    <PageLayout fullBleed footer={false}>
      {/* Nav */}
      <NavHeader />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <ShootingStars />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 50% 0%, hsl(38 65% 57% / 0.12), transparent)',
          }}
        />
        <div className="relative z-10 container text-center">
          <BlurFade delay={0.05}>
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-6 tracking-widest uppercase"
              style={{
                background: 'hsl(38 65% 57% / 0.1)',
                border: '1px solid hsl(38 65% 57% / 0.25)',
                color: 'hsl(43 75% 68%)',
              }}
            >
              <Trophy size={10} />
              Hall of Discipline
            </div>
          </BlurFade>

          <BlurFade delay={0.12}>
            <h1
              className="mb-4"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 'clamp(2.8rem, 6vw, 5rem)',
                fontWeight: 600,
                background:
                  'linear-gradient(135deg, hsl(40 15% 92%) 0%, hsl(43 75% 68%) 50%, hsl(38 65% 57%) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.05,
              }}
            >
              Selfless Leaderboard
            </h1>
          </BlurFade>

          <BlurFade delay={0.2}>
            <p
              className="text-base max-w-lg mx-auto leading-relaxed mb-4"
              style={{ color: 'hsl(35 8% 52%)' }}
            >
              Every SOL, BONK, or SKR locked — immortalised as Selfless Points.
              Lock longer, donate generously, and rise through the ranks.
            </p>
          </BlurFade>

          {/* My rank bar */}
          {myEntry && myRank > 0 && (
            <BlurFade delay={0.28}>
              <div
                className="inline-flex items-center gap-4 px-6 py-3 rounded-xl mt-2"
                style={{
                  background: 'hsl(38 65% 57% / 0.1)',
                  border: '1px solid hsl(38 65% 57% / 0.3)',
                }}
              >
                <div className="text-left">
                  <div className="text-xs uppercase tracking-widest mb-0.5" style={{ color: 'hsl(35 8% 45%)' }}>
                    Your Rank
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(43 75% 68%)' }}
                  >
                    #{myRank}
                    <span className="text-sm font-normal ml-1" style={{ color: 'hsl(35 8% 45%)' }}>
                      of {leaderboard.length}
                    </span>
                  </div>
                </div>
                <div
                  className="w-px h-10 self-center"
                  style={{ background: 'hsl(38 65% 57% / 0.2)' }}
                />
                <div className="text-left">
                  <div className="text-xs uppercase tracking-widest mb-0.5" style={{ color: 'hsl(35 8% 45%)' }}>
                    Your Points
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(38 65% 57%)' }}
                  >
                    {formatPoints(myEntry.totalPoints)}
                  </div>
                </div>
              </div>
            </BlurFade>
          )}
        </div>
      </section>

      <div className="container pb-20">
        {loading && locks.length === 0 ? (
          /* Loading state */
          <div className="text-center py-20" style={{ color: 'hsl(35 8% 40%)' }}>
            <Trophy size={40} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm">Loading leaderboard…</p>
          </div>
        ) : leaderboard.length === 0 ? (
          /* Empty state */
          <BlurFade delay={0.1}>
            <div className="text-center py-24">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'hsl(38 65% 57% / 0.1)',
                  border: '1px solid hsl(38 65% 57% / 0.2)',
                }}
              >
                <Trophy size={30} style={{ color: 'hsl(38 65% 57%)' }} />
              </div>
              <h3
                className="text-2xl font-semibold mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                No locks yet
              </h3>
              <p className="text-sm mb-6" style={{ color: 'hsl(35 8% 45%)' }}>
                Be the first to lock SOL, BONK, or SKR and claim the top spot.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                  color: 'hsl(30 8% 4%)',
                  border: 'none',
                }}
              >
                Start Locking
              </Button>
            </div>
          </BlurFade>
        ) : (
          <>
            {/* Top 3 Podium */}
            {top3.length > 0 && (
              <BlurFade delay={0.1}>
                <div className="mb-14">
                  <div className="flex items-center gap-2 mb-6">
                    <Crown size={16} style={{ color: 'hsl(43 75% 68%)' }} />
                    <h2
                      className="text-xl font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(43 75% 68%)' }}
                    >
                      Podium
                    </h2>
                  </div>
                  {/* Podium layout: 2nd | 1st | 3rd */}
                  <div
                    className="grid gap-4"
                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
                  >
                    {top3.map((entry, idx) => {
                      const rank = (idx + 1) as 1 | 2 | 3;
                      return (
                        <PodiumCard
                          key={entry.address}
                          entry={entry}
                          rank={rank}
                          isMe={entry.address === myAddress}
                        />
                      );
                    })}
                  </div>
                </div>
              </BlurFade>
            )}

            {/* Table header */}
            {rest.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Trophy size={15} style={{ color: 'hsl(35 8% 45%)' }} />
                  <h2
                    className="text-lg font-semibold"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 75%)' }}
                  >
                    Ranks 4–{Math.min(100, leaderboard.length)}
                  </h2>
                </div>

                {/* Column headers */}
                <div
                  className="grid px-5 py-2 mb-2 text-xs font-medium uppercase tracking-wider"
                  style={{
                    gridTemplateColumns: '2.5rem 1fr auto auto auto',
                    color: 'hsl(35 8% 38%)',
                    gap: '0.75rem',
                  }}
                >
                  <span>#</span>
                  <span>Address</span>
                  <span className="text-right hidden sm:block">PermaLOCKs</span>
                  <span className="text-right hidden md:block">Locks</span>
                  <span className="text-right">Points</span>
                </div>

                <div className="space-y-1.5">
                  {rest.map((entry, idx) => (
                    <TableRow
                      key={entry.address}
                      entry={entry}
                      rank={idx + 4}
                      isMe={entry.address === myAddress}
                      delay={0.02 * idx}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* If user is ranked but not visible in top 100 */}
            {myEntry && myRank > 100 && (
              <BlurFade delay={0.2}>
                <div
                  className="mt-8 rounded-xl p-5"
                  style={{
                    background: 'hsl(38 65% 57% / 0.07)',
                    border: '1px solid hsl(38 65% 57% / 0.2)',
                  }}
                >
                  <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'hsl(35 8% 45%)' }}>
                    Your position
                  </p>
                  <TableRow
                    entry={myEntry}
                    rank={myRank}
                    isMe
                    delay={0}
                  />
                </div>
              </BlurFade>
            )}
          </>
        )}

        {/* Back link */}
        <BlurFade delay={0.4}>
          <div className="mt-12 text-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm transition-colors"
              style={{ color: 'hsl(35 8% 45%)' }}
            >
              <ArrowLeft size={14} />
              Back to Dashboard
            </Link>
          </div>
        </BlurFade>
      </div>

      {/* Footer */}
      <footer
        className="py-6 px-8 text-center"
        style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(35 8% 36%)' }}
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <Lock size={11} style={{ color: 'hsl(38 65% 57%)' }} />
          <span className="text-xs" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            SelfWorth · Selfless Points are computed client-side from on-chain lock data
          </span>
        </div>
      </footer>
    </PageLayout>
  );
};

export default LeaderboardPage;
